import { ICanvasPage } from '@src/js/auto-test/canvas-e2e/interface/canvas-page.interface';
import { CanvasEntry, CanvasLocator, IndexType } from '@src/js/auto-test/canvas-e2e/interface/common.interface';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { Container, DisplayObject } from '@tencent/alloy-renderer';
import { findComponentsInContainerById } from '@src/js/auto-test/canvas-e2e/utils';
import CanvasElement from '@src/js/auto-test/canvas-e2e/canvas-element';
import { PlayerRenderService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player';
import ParagraphMoreDialog from '@src/slide/application/service/paragraph-manager/core/paragraph-more-dialog';
import OperationRenderServiceManager from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/operation-render-service-manager';
import CanvasE2EManager from '@src/js/auto-test/canvas-e2e/index';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { LayerType } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-constants';
import { RendererChartComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-chart-component';
import ChartElement from './canvas-element/chart';
import {
    ICanvasRendererService,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/base/canvas-renderer-service.interface';
import {
    IPcEditorAreaService,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/pc-content-service.interface';
import {
    IMobileRendererService,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/mobile-renderer-service.interface';
import AnimE2EManager, { IAttr } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/player-state-animation/anim-e2e-manager';

/**
 * 封装一层查找统一渲染层组件UI接口，供E2E测试调用
 */
export default class CanvasPage implements ICanvasPage {
    private selectionService: ISelectionService | undefined;
    private contentRendererService: ICanvasRendererService | undefined;

    /**
     * 查找规则：
     * 1. slideIndex有，则先查找所有对应的slideComponent
     * 2. parentId存在，先查找parent component
     * 2. id存在。如何2存在，查找结果2中所有id对应的component，否则查找slide中对应component
     * 3. findIndex存在，则过滤2中结果，查找对应findIndex的component
     * @param locator
     */
    public findElement(locator: CanvasLocator): CanvasElement | undefined {
        return this.findElements(locator)[0];
    }

    public findElements(locator: CanvasLocator): CanvasElement[] {
        const { entry = CanvasEntry.edit, slideIndex = 'current', parentId, id, findIndex, customizer, layerType } = locator;
        const rootComponents: Container[] = this.findRootComponents(entry, slideIndex, layerType);
        let components: DisplayObject[] = [];
        id && rootComponents.forEach((root: Container) => {
            if (!root) return;
            let parents: Container[] = [root];
            if (parentId) {
                parents = findComponentsInContainerById(root, parentId) as Container[];
            }
            parents.forEach((parent: Container) => {
                const findComps = findComponentsInContainerById(parent, id);
                components.push(...findComps);
            });
        });

        if (!expect(findIndex).isNullOrUndefined()) {
            const findIndexArray: number[] = [];
            if (expect(findIndex).isNumber()) {
                findIndexArray.push(findIndex as number);
            } else if (expect(findIndex).isArray()) {
                const indexArray = findIndex as number[];
                findIndexArray.push(...indexArray);
            }
            components = components.filter((slide, index) => findIndexArray.includes(index));
        }
        if (customizer) {
            components = customizer(components);
        }
        return components.map((comp: DisplayObject) => this.getCanvasElement(comp));
    }

    public setServiceDelegate(services: {
        selectionService: ISelectionService;
        rendererService: ICanvasRendererService
    }): void {
        if (!this.selectionService) {
            this.selectionService = services.selectionService;
        }
        if (!this.contentRendererService) {
            this.contentRendererService = services.rendererService;
        }
    }

    /**
     * 获取PPT总页数
     */
    public getSlideTotalNum() {
        const slideList = this.selectionService?.slides || [];
        return slideList.length;
    }

    /**
     * 获取当前页页数
     * @param entry CanvasEntry
     * @returns number
     */
    public getCurrentPageIndex(entry: CanvasEntry): number {
        let curIndex = 0;
        switch (entry) {
            case CanvasEntry.edit:
            case CanvasEntry.list:
            case CanvasEntry.layoutEdit:
                if (this.selectionService?.currentSlideIndex !== undefined) {
                    curIndex = this.selectionService?.currentSlideIndex;
                }
                break;
            case CanvasEntry.player: {
                const playStore = PlayerRenderService.getInstance().getPlayerReduxStore();
                const state = playStore.getState();
                curIndex = state?.control?.currentPage || 0;
                break;
            }
        }
        return curIndex;
    }

    /**
     * 暂停动画
     * @param duration 暂停的时间刻度，单位s
     */
    public pauseAnim(duration: number): void {
        const timeline = this.getCurrentMainAnimTimeline();
        timeline?.pause(duration);
    }

    /**
     * 重新开始动画
     */
    public restartAnim(): void {
        const timeline = this.getCurrentMainAnimTimeline();
        timeline?.restart();
    }

    /**
     * 获取动画开始时元素的属性对象
     * @returns Record<string, IAttr> | undefined
     */
    public getElementStartAttrObj(): Record<string, IAttr> | undefined {
        const timeline = this.getCurrentMainAnimTimeline();
        if (!timeline) return;
        const currentOrder = timeline?.getCurrentOrder();
        const animE2EManager = AnimE2EManager.getInstance();
        const elementAttrMap = animE2EManager.getElementStartAttrObj(currentOrder);
        return elementAttrMap;
    }

    private getCanvasElement(comp: DisplayObject) {
        if (comp instanceof RendererChartComponent) {
            return new ChartElement(comp);
        }
        return new CanvasElement(comp);
    }

    /**
     * 通过slideIndex，获取到匹配的root component
     * @param entry
     * @param slideIndex
     */
    private findRootComponents(
        entry: CanvasEntry,
        slideIndex: IndexType | undefined,
        layerType?: LayerType,
    ): Container[] {
        const slideIndexArray: number[] = [];
        if (expect(slideIndex).isNumber()) {
            slideIndexArray.push(slideIndex as number);
        } else if (expect(slideIndex).isArray()) {
            const indexArray = slideIndex as number[];
            slideIndexArray.push(...indexArray);
        } else if (slideIndex === 'current') {
            slideIndexArray.push(this.getCurrentPageIndex(entry));
        }
        const slideList = this.selectionService?.slides || [];
        const findSlides = slideList.filter((slide, index) => slideIndexArray.includes(index));
        return this.getRootCompWithEntry(entry, findSlides, layerType);
    }


    /* eslint complexity: ["error", 15] */
    private getRootCompWithEntry(entry: CanvasEntry, findSlides, layerType?: LayerType): Container[] {
        const rootComponents: Container[] = [];
        switch (entry) {
            case CanvasEntry.edit: {
                if (!browser.isMobile) {
                    const service = this.contentRendererService as IPcEditorAreaService;
                    const roots = findSlides.map((slide) => {
                        const slideEditor = service.getSlideEditorByKey(slide.key);
                        return slideEditor?.application.stage;
                    });
                    rootComponents.push(...roots);
                    break;
                } else {
                    const service = this.contentRendererService as IMobileRendererService;
                    const root = service.application.stage;
                    rootComponents.push(root);
                    break;
                }
            }
            case CanvasEntry.layoutEdit: {
                const service = this.contentRendererService as IPcEditorAreaService;
                const roots = findSlides.map((slide) => {
                    const slideEditor = service.getLayoutEditorByKey(slide.key);
                    return slideEditor?.application.stage;
                });
                rootComponents.push(...roots);
                break;
            }
            case CanvasEntry.list: {
                const appMap = CanvasE2EManager.getInstance().getThumbApplications();
                if (appMap) {
                    const roots = findSlides.map((slide) => {
                        const app = appMap.get(slide);
                        return app?.stage;
                    });
                    rootComponents.push(...roots);
                }
                break;
            }
            case CanvasEntry.operation: { // 操作区
                const service = OperationRenderServiceManager.getInstance().operationLayer;
                const root = service.getApplication().stage;
                rootComponents.push(root);
                break;
            }
            case CanvasEntry.note: {
                const root = window.noteApp?.stage;
                root && rootComponents.push(root);
                break;
            }
            case CanvasEntry.player: {
                const service = PlayerRenderService.getInstance();
                const roots = findSlides.map((slide) => {
                    if (layerType === LayerType.OPERATION) {
                        return slide?.mainSlideTimeline?.getCurrentAnimApp()?.stage;
                    }
                    const slidePlayer = service.getRenderTree(slide);
                    return slidePlayer?.getApplication().stage;
                });
                rootComponents.push(...roots);
                break;
            }
            case CanvasEntry.paragraphPreview: {
                const { previewRendererApplication } = ParagraphMoreDialog;
                previewRendererApplication && rootComponents.push(previewRendererApplication.stage);
                break;
            }
        }
        return rootComponents;
    }

    /**
     * 获取演示态当前演示页的主队列动画时间线
     */
    private getCurrentMainAnimTimeline() {
        const slideIndex = this.getCurrentPageIndex(CanvasEntry.player);
        const slideModel = PlayerRenderService.getInstance().getSlideModelByIndex(slideIndex);
        return slideModel?.mainSlideTimeline;
    }
}
